import React from 'react';
import { reportClientError } from '../utils/clientErrorReporter';

/**
 * Global error boundary.
 * Shows a friendly error screen (not a blank page) and offers a reload.
 * Dev-only: shows the stack trace too.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    // Error Watchdog (Aug 2026): explicit crash report tagged for the
    // Platform HQ dashboard alert.
    try {
      reportClientError(`Crash: ${error?.message || error}`, {
        kind: 'crash',
        componentStack: String(errorInfo?.componentStack || '').slice(0, 2000),
      });
    } catch { /* never throw from the reporter */ }
    this.setState({ errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const isDev = process.env.NODE_ENV !== 'production';
      return (
        <div
          className="min-h-screen flex items-center justify-center p-6"
          style={{ background: 'var(--ht-bg, #0B1321)', color: 'var(--ht-text, #fff)' }}
          data-testid="error-boundary-fallback"
        >
          <div
            className="max-w-md w-full p-6 rounded-2xl"
            style={{ background: '#1A2433', border: '1px solid #2D3748' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.15)' }}>
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <h1 className="text-xl font-bold">Something went wrong</h1>
            </div>
            <p className="text-sm mb-4" style={{ color: '#A0AEC0' }}>
              This page hit an unexpected error. You can reload the page to try again, or go back to the dashboard.
            </p>
            {/* Always show a one-line error summary so production tickets are
                actionable. The full stack stays dev-only to avoid leaking
                internal paths. */}
            {this.state.error && (
              <div
                className="mb-4 text-xs rounded-lg p-3 font-mono break-words"
                style={{ background: '#0F1923', border: '1px solid #2D3748', color: '#FBBF24' }}
                data-testid="error-summary"
              >
                {String(this.state.error?.message || this.state.error)}
              </div>
            )}
            {isDev && this.state.error && (
              <details className="mb-4 text-xs rounded-lg p-3" style={{ background: '#0F1923', border: '1px solid #2D3748', color: '#CBD5E1' }}>
                <summary className="cursor-pointer font-medium">Full stack trace (dev only)</summary>
                <pre className="mt-2 whitespace-pre-wrap break-all" style={{ fontSize: 11 }}>
                  {String(this.state.error?.message || this.state.error)}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={this.handleReload}
                className="flex-1 px-4 py-2.5 rounded-lg font-medium text-white"
                style={{ background: '#1E88E5' }}
                data-testid="error-reload-btn"
              >
                Reload page
              </button>
              <button
                type="button"
                onClick={this.handleGoHome}
                className="px-4 py-2.5 rounded-lg font-medium"
                style={{ background: 'transparent', color: '#A0AEC0', border: '1px solid #2D3748' }}
                data-testid="error-home-btn"
              >
                Go home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
